import { useEffect, useState } from "react";
import { api, fmtUSD } from "@/lib/api";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "set", "oct", "nov", "dic"];
const mesCorto = (p) => `${MESES[Number(p.slice(5, 7)) - 1]} ${p.slice(2, 4)}`;

// Evolución mensual de cobranzas (serie real de /report/cobranzas-acumuladas → por_mes).
export const EvolucionCard = ({ vendedor, mes }) => {
  const [d, setD] = useState(null);
  const [rango, setRango] = useState(12);
  useEffect(() => {
    let vivo = true;
    api.get("/report/cobranzas-acumuladas", { params: { vendedor, hasta: mes } }).then((r) => { if (vivo) setD(r.data); }).catch(() => { if (vivo) setD({ por_mes: {} }); });
    return () => { vivo = false; };
  }, [vendedor, mes]);
  const serie = Object.entries(d?.por_mes || {}).sort().slice(-rango).map(([p, v]) => ({ mes: mesCorto(p), valor: v }));
  return (
    <section data-testid="panel-evolucion" className="rounded-2xl bg-white p-6 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-slate-900 leading-snug">Evolución de cobranzas</h3>
          <p className="text-xs text-slate-500">Cobrado por mes (USD) · acumulado {d?.total != null ? fmtUSD(d.total) : "—"}</p>
        </div>
        <div className="inline-flex rounded-full bg-slate-100 p-1 shrink-0">
          {[6, 12].map((n) => (
            <button key={n} onClick={() => setRango(n)} data-testid={`evolucion-rango-${n}`} className={`px-3.5 py-1.5 rounded-full text-xs font-bold ${rango === n ? "tm-negra" : "text-slate-600"}`}>{n}M</button>
          ))}
        </div>
      </div>
      <div className="h-56">
        {serie.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={serie} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs><linearGradient id="tmRojo" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B8402A" stopOpacity={0.35} /><stop offset="100%" stopColor="#B8402A" stopOpacity={0.02} /></linearGradient></defs>
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#5B6858" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#5B6858" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}k` : v)} />
              <Tooltip formatter={(v) => [fmtUSD(v), "Cobrado"]} contentStyle={{ borderRadius: 16, border: "1px solid #D9E3D6", background: "#fff", fontSize: 12 }} />
              <Area type="monotone" dataKey="valor" stroke="#B8402A" strokeWidth={2.5} fill="url(#tmRojo)" dot={false} activeDot={{ r: 5, fill: "#B8402A" }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : <div className="h-full flex items-center justify-center text-sm text-slate-500">Sin cobranzas cargadas todavía.</div>}
      </div>
    </section>
  );
};

const Anillo = ({ pct }) => {
  const r = 44, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, pct || 0));
  return (
    <svg viewBox="0 0 110 110" className="h-32 w-32 shrink-0">
      <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="11" />
      <circle cx="55" cy="55" r={r} fill="none" stroke="#F3F7F2" strokeWidth="11" strokeLinecap="round" strokeDasharray={`${(c * p) / 100} ${c}`} transform="rotate(-90 55 55)" />
      <text x="55" y="61" textAnchor="middle" fontSize="24" fontWeight="800" fill="#fff">{Math.round(p)}%</text>
    </svg>
  );
};

// Avance de documentos: datos reales de /clientes (gestión del encargado o, si no hay, conteo por estado).
export const DocsAnillo = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/clientes").then((r) => setD(r.data)).catch(() => setD({ items: [], por_vendedor: [] })); }, []);
  const items = d?.items || [];
  const ev = items.filter((c) => c.estado !== "EXENTO (directorio)" && c.estado !== "NO CORRESPONDE");
  const solicitados = ev.filter((c) => c.estado !== "SIN SOLICITAR").length;
  const recibidos = ev.filter((c) => c.estado === "RECIBIDO").length;
  const g = d?.gestion;
  const pctSol = g?.cobertura_pct ?? (ev.length ? Math.round((100 * solicitados) / ev.length) : 0);
  const pctRec = g?.recepcion_pct ?? (ev.length ? Math.round((100 * recibidos) / ev.length) : 0);
  const puntos = g ? g.puntos : (d?.por_vendedor || []).reduce((a, v) => a + (v.puntos || 0), 0);
  return (
    <section data-testid="panel-docs" className="rounded-2xl bg-[#007336] text-white p-6 flex flex-wrap items-center gap-6 min-w-0">
      {d ? <Anillo pct={pctSol} /> : <div className="h-32 w-32 rounded-full border-[11px] border-white/25 animate-pulse" />}
      <div className="min-w-0 flex-1">
        <h3 className="text-lg font-bold leading-snug">Documentos de clientes</h3>
        <p className="text-sm text-white/80 mb-3">Solicitados sobre el total evaluable {d ? `(${ev.length} clientes)` : ""}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <div><div className="text-2xl font-extrabold tabular-nums leading-none">{pctRec}%</div><div className="text-xs text-white/80 mt-1">con visto bueno</div></div>
          <div><div className="text-2xl font-extrabold tabular-nums leading-none">{puntos > 0 ? `+${puntos}` : puntos}</div><div className="text-xs text-white/80 mt-1">puntos {g ? "de la gestión" : "del equipo"}</div></div>
        </div>
      </div>
    </section>
  );
};

// Antigüedad de la cartera: a vencer (KPI) + tramos reales de Deudas +60 días; "vencido ≤60" = vencido total − tramos.
export const AntiguedadCard = ({ kpis, fechaCorte, vendedor }) => {
  const [tr, setTr] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get("/cartera/deudas-60", { params: { fecha_corte: fechaCorte, vendedor } }).then((r) => { if (vivo) setTr(r.data?.por_tramo || {}); }).catch(() => { if (vivo) setTr({}); });
    return () => { vivo = false; };
  }, [fechaCorte, vendedor]);
  const t = tr || {};
  const mas60 = Object.values(t).reduce((a, v) => a + (Number(v) || 0), 0);
  const seg = [
    { k: "A vencer", v: Number(kpis?.valor_a_vencer) || 0, c: "#007336" },
    { k: "Vencido ≤60 días", v: Math.max(0, (Number(kpis?.valor_vencido) || 0) - mas60), c: "#5FA8D9" },
    { k: "61–180 días", v: (Number(t["61-90 días"]) || 0) + (Number(t["91-180 días"]) || 0), c: "#B8402A" },
    { k: "Más de 180 días", v: (Number(t["181-365 días"]) || 0) + (Number(t["Más de 365 días"]) || 0), c: "#24331F" },
  ];
  const total = seg.reduce((a, s) => a + s.v, 0);
  return (
    <section data-testid="panel-antiguedad" className="rounded-2xl bg-white p-6 min-w-0">
      <h3 className="text-lg font-bold text-slate-900 leading-snug">Antigüedad de la cartera</h3>
      <p className="text-xs text-slate-500 mb-4">Reparto de la cartera abierta (USD) · total {fmtUSD(total)}</p>
      <div className="flex h-4 w-full rounded-full overflow-hidden bg-slate-100">
        {seg.map((s) => s.v > 0 && <div key={s.k} style={{ width: `${(100 * s.v) / (total || 1)}%`, background: s.c }} title={`${s.k}: ${fmtUSD(s.v)}`} />)}
      </div>
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {seg.map((s) => (
          <li key={s.k} className="flex items-center justify-between gap-3 min-w-0">
            <span className="flex items-center gap-2 text-sm text-slate-600 min-w-0"><span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: s.c }} />{s.k}</span>
            <span className="text-sm font-bold tabular-nums text-slate-900 whitespace-nowrap">{fmtUSD(s.v)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};
